import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import './LoanPayment.css';
import { formatDateToDDMMYYYY } from '../../utils/dateUtils';
import { maskAadhaar, maskPAN } from '../../utils/sensitiveDataUtils';

const LoanPayment = () => {
  const { id, type } = useParams();
  const searchType = type || 'name';
  const [loanDetails, setLoanDetails] = useState(null);
  const [emiDetails, setEmiDetails] = useState([]);
  const [paidDates, setPaidDates] = useState([]);
  const [showOverdueInterest, setShowOverdueInterest] = useState(false);
  const [overdueInterestRate, setOverdueInterestRate] = useState(15); // Default 15% per month
  const [overdueDetails, setOverdueDetails] = useState({ rate: 15, details: [] });
  const [overduePayAmounts, setOverduePayAmounts] = useState({}); // keyed by due_date
  const [selectedDueDate, setSelectedDueDate] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [isPaymentSuccessful, setIsPaymentSuccessful] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [showSlipModal, setShowSlipModal] = useState(false);
  const [slipFilterDueDate, setSlipFilterDueDate] = useState(null);
  const [lastInsertedPayments, setLastInsertedPayments] = useState([]);

  useEffect(() => {
    const fetchLoanDetails = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/loan-reciever`, {
          params: { type: searchType, value: id },
        });
        setLoanDetails(response.data);
      } catch (error) {
        console.error('Error fetching loan details:', error);
      }
    };

    fetchLoanDetails();
  }, [id, searchType]);

  useEffect(() => {
    if (loanDetails && loanDetails.account_no) {
      const fetchEmiDetails = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/emi-details/${loanDetails.account_no}`);
          console.log('EMI details:', response.data);
          setEmiDetails(response.data);
        } catch (error) {
          console.error('Error fetching EMI details:', error);
        }
      };

      const fetchPaidDates = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/fetch-paid-status/${loanDetails.account_no}`);
          setPaidDates(response.data);
        } catch (error) {
          console.error('Error fetching paid status:', error);
        }
      };

      const fetchTransactions = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/emi-transactions/${loanDetails.account_no}`);
          console.log('Transactions:', response.data);
          setTransactions(response.data || []);
        } catch (error) {
          console.error('Error fetching transactions:', error);
        }
      };

      fetchEmiDetails();
      fetchPaidDates();
      fetchTransactions();
    }
  }, [loanDetails]);

  // Fetch overdue details from backend (dynamic rate)
  useEffect(() => {
    const fetchOverdueDetails = async () => {
      if (!loanDetails || !loanDetails.account_no) return;
      try {
        const response = await axios.get(`${API_BASE_URL}/overdue-details/${loanDetails.account_no}`, {
          params: { rate: overdueInterestRate }
        });
        console.log('Overdue details:', response.data);
        setOverdueDetails(response.data);
      } catch (error) {
        console.error('Error fetching overdue details:', error);
      }
    };
    fetchOverdueDetails();
  }, [loanDetails, overdueInterestRate]);
  

  // Handle overdue payment submission
  const handleOverduePayment = async (detail) => {
    try {
      const amount = Number(overduePayAmounts[detail.due_date] || 0);
      if (!amount || amount <= 0) {
        alert('Enter a valid overdue payment amount.');
        return;
      }
      await axios.post(`${API_BASE_URL}/pay-overdue`, {
        account_no: loanDetails.account_no,
        due_date: detail.due_date,
        amount,
        payment_mode: 'Overdue',
        transaction_id: `OD-${Date.now()}`,
      });
      alert('Overdue Payment Successful!');
      // Refresh overdue details
      const odRes = await axios.get(`${API_BASE_URL}/overdue-details/${loanDetails.account_no}`, { params: { rate: overdueInterestRate } });
      setOverdueDetails(odRes.data);
    } catch (error) {
      console.error('Error processing overdue payment:', error);
      alert('Overdue Payment failed. Please try again.');
    }
  };

  // Handle payment submission
  const handlePayment = async () => {
    if (!selectedDueDate || !paymentMode) {
      alert('Please select due date and payment mode.');
      return;
    }
    const emiRow = emiDetails.find(e => e.due_date === selectedDueDate);
    if (!emiRow) {
      alert('Selected due date is not available.');
      return;
    }
    const remaining = Number(emiRow.remaining_amount);
    const amount = Number(paymentAmount);
    if (!amount || amount <= 0) {
      alert('Enter a valid payment amount.');
      return;
    }
    // Allow excess payments - excess will be automatically applied to next EMI

    try {
      const response = await axios.post(`${API_BASE_URL}/pay-emi`, {
        account_no: loanDetails.account_no,
        due_date: selectedDueDate,
        payment_mode: paymentMode,
        transaction_id: transactionId.trim() || null,
        amount: Number(amount),
      });
      // Show success message with excess payment info if applicable
      const successMessage = response.data?.message || 'Payment Successful!';
      alert(successMessage);
      const newPayments = response.data?.payments || [];
      setLastInsertedPayments(newPayments);
      setIsPaymentSuccessful(true);
      setSelectedDueDate('');
      setPaymentMode('');
      setTransactionId('');
      setPaymentAmount('');
      // Refresh EMI details and transactions
      const [emiRes, txRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/emi-details/${loanDetails.account_no}`),
        axios.get(`${API_BASE_URL}/emi-transactions/${loanDetails.account_no}`)
      ]);
      setEmiDetails(emiRes.data);
      setTransactions(txRes.data || []);

      // Print a separate slip for each newly inserted payment (use refreshed EMI totals)
      if (newPayments.length > 0) {
        newPayments.forEach((payment) =>
          generateSlip({ payment, emiSnapshot: emiRes.data })
        );
      }
    } catch (error) {
      const serverError = error.response?.data?.error;
      const serverDetails = error.response?.data?.details;
      if (serverError === 'Paid') {
        alert('Already Paid');
      } else {
        console.error('Error processing payment:', error.response?.data || error);
        alert(serverError || serverDetails || 'Payment failed. Please try again.');
      }
    }
  };

  // Generate and print payment slip (single transaction)
  const generateSlip = (opts = {}) => {
    const { payment, emiSnapshot } = opts; // payment: { amount, payment_date, payment_mode, transaction_id, due_date }
    const amountToShow = payment?.amount ?? 0;
    const dateToShow = payment?.payment_date ? new Date(payment.payment_date) : new Date();
    const modeToShow = payment?.payment_mode || paymentMode || '';
    const txToShow = payment?.transaction_id || transactionId || '';
    const emisForBalance = emiSnapshot || emiDetails;

    // Calculate total remaining balance (sum of all remaining amounts from all EMIs)
    const totalRemainingBalance = emisForBalance.reduce((sum, emi) => {
      const remaining = Number(emi.remaining_amount || 0);
      // If remaining is <= 5, treat as 0 (already paid)
      return sum + (remaining <= 5 ? 0 : remaining);
    }, 0);

    const slipContent = `
  
<style>
  body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
  .container { display: flex; }
  .left-receipt { width: 40%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
  .right-receipt { width: 60%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
  .header { text-align: center; font-weight: bold; font-size: 1.2em; margin-bottom: 5px; }
  .sub-header { text-align: center; font-size: 0.9em; margin-bottom: 15px; }
  .details { margin: 10px 0; }
  .line { display: flex; justify-content: space-between; margin: 5px 0; }
  .table { width: 100%; border-collapse: collapse; margin-top: 10px; }
  .table td { padding: 5px; }
  .table .right { text-align: right; font-weight: bold; }
  .total { border-top: 1px solid #000; margin-top: 10px; padding-top: 5px; font-weight: bold; }
  .footer { text-align: center; margin-top: 15px; font-size: 0.9em; }
  .balance { font-weight: bold; font-size: 1.3em; margin-top: 10px; text-align: center; }
</style>

<div class="container">
  <!-- Left Receipt -->
  <div class="left-receipt">
    <div class="header">M/s KALABHAIRAV FINANCE AND LEASING (R)</div>
    <div class="sub-header">Khajuri, Aland , Kalaburgi</div>
    <div class="details">
      <div class="line"><strong>A/C No:</strong> ${loanDetails.account_no}</div>
      <div class="line"><strong>Date:</strong> ${formatDateToDDMMYYYY(new Date())}</div>
    </div>
    <div class="details">
    <div class="line"><strong>Received from Sri:</strong> ${loanDetails.loanee_name}</div>
    <div class="line"><strong>Amount Rs:</strong> ₹${Number(amountToShow).toFixed(2)}</div>
   </div>
    <table class="table">
        <tr>
          <td>EMI PAYMENT</td>
          <td class="right">${Number(amountToShow).toFixed(2)}</td>
        </tr>
      </table>
      <div class="total">
        TOTAL: ₹${Number(amountToShow).toFixed(2)}
      </div>
      <div class="balance">
        Bal: ₹${totalRemainingBalance.toFixed(2)}
      </div>
    <div class="footer">
      Cashier / Mg. Partner                      
    </div>
  </div>

  <!-- Right Receipt -->
  <div class="right-receipt">
    <div class="header">M/s KALABHAIRAV FINANCE AND LEASING (R)</div>
    <div class="sub-header">Khajuri, Aland , Kalaburgi</div>
    <div class="details">
      <div class="line"><strong>A/C No:</strong> ${loanDetails.account_no}</div>
      <div class="line"><strong>Date:</strong> ${formatDateToDDMMYYYY(new Date())}</div>
    </div>
   <div class="details">
     
      <div class="line"><strong>Received from Sri:</strong> ${loanDetails.loanee_name}</div>
      <div class="line"><strong>Amount Rs:</strong> ₹${Number(amountToShow).toFixed(2)}</div>
    </div>
    <table class="table">
      <tr>
          <td>EMI PAYMENT</td>
          <td class="right">${Number(amountToShow).toFixed(2)}</td>
        </tr>
      </table>
      <div class="total">
        TOTAL: ₹${Number(amountToShow).toFixed(2)}
      </div>
      <div class="balance">
        Bal: ₹${totalRemainingBalance.toFixed(2)}
      </div>
    <div class="footer">
      Cashier / Mg. Partner
    </div>
  </div>
</div>
`;
  const printWindow = window.open('', '', 'height=800,width=1200');
  printWindow.document.write('<html><head><title>Interest Slip</title></head><body>');
  printWindow.document.write(slipContent);
  printWindow.document.write('</body></html>');
  printWindow.document.close();
  printWindow.print();
};

  const viewSlipForDueDate = (dueDate) => {
    const txs = transactions.filter((t) => t.due_date === dueDate);
    if (txs.length === 0) {
      alert('No transactions found for this EMI.');
      return;
    }
    // Open slips list for this EMI so each payment can be printed separately
    setSlipFilterDueDate(dueDate);
    setShowSlipModal(true);
  };

  const openAllSlips = () => {
    setSlipFilterDueDate(null);
    setShowSlipModal(true);
  };
  const closeAllSlips = () => {
    setShowSlipModal(false);
    setSlipFilterDueDate(null);
  };

  // Toggle overdue interest table on Ctrl + T key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'b') {
        setShowOverdueInterest((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!loanDetails) {
    return <h2>Loading loan details...</h2>;
  }

  return (
    <div className="loan-payment-container">
      <div className="loan-payment-header">Loan Payment</div>

      {/* Loan Details */}
      <div className="loan-details">
        <p><strong>Account No:</strong> {loanDetails.account_no}</p>
        <p><strong>Loanee Name:</strong> {loanDetails.loanee_name}</p>
        <p><strong>Date:</strong> {formatDateToDDMMYYYY(loanDetails.date)}</p>
        <p><strong>Aadhaar Debtor:</strong> {maskAadhaar(loanDetails.aadhaar_debtor)}</p>
        <p><strong>PAN Card:</strong> {maskPAN(loanDetails.pan_card)}</p>
        <p><strong>Address Debtor:</strong> {loanDetails.address_debtor}</p>
        <p><strong>Guarantor Name:</strong> {loanDetails.guarantor_name}</p>
        <p><strong>Loan Amount:</strong> ₹{loanDetails.loan_amount}</p>
        <p><strong>Period:</strong> {loanDetails.period} months</p>
      </div>

      {/* EMI Details */}
      <div className="emi-details">
        <h3>EMI Details</h3>
        <table>
          <thead>
            <tr>
              <th>Due Date</th>
              <th>EMI Amount</th>
              <th>Paid</th>
              <th>Remaining</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {emiDetails.map((emi, index) => (
              <tr key={index}>
                <td>{formatDateToDDMMYYYY(emi.due_date)}</td>
                <td>₹{parseFloat(emi.monthly_installment).toFixed(2)}</td>
                <td>₹{Number(emi.paid_amount || 0).toFixed(2)}</td>
                <td>₹{Number(emi.remaining_amount || 0).toFixed(2)}</td>
                <td>
                  <span className={`status-badge ${emi.status === 'Paid' ? 'status-paid' : emi.status === 'Partially Paid' ? 'status-partial' : 'status-unpaid'}`}>
                    {emi.status}
                  </span>
                </td>
                <td>
                  {Number(emi.paid_amount) > 0 && (
                    <button onClick={() => viewSlipForDueDate(emi.due_date)}>
                      {transactions.filter((t) => t.due_date === emi.due_date).length > 1
                        ? 'View Slips'
                        : 'View Slip'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Overdue Interest Details */}
      {showOverdueInterest && (
        <div className="overdue-interest-details">
          <h3>Overdue Interest Details</h3>
          <div>
            <label>Overdue Interest Rate (%):</label>
            <input
              type="number"
              value={overdueInterestRate}
              onChange={(e) => setOverdueInterestRate(Number(e.target.value))}
            />
          </div>
          <table>
            <thead>
              <tr>
                <th>Due Date</th>
                <th>Remaining Amount</th>
                <th>Days Due</th>
                <th>Total Overdue Interest</th>
                <th>Overdue Paid</th>
                <th>Remaining Overdue</th>
                <th>Payment Amount</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {(overdueDetails.details || []).map((detail, index) => (
                <tr key={index}>
                  <td>{formatDateToDDMMYYYY(detail.due_date)}</td>
                  <td>₹{Number(detail.remaining_amount).toFixed(2)}</td>
                  <td>{detail.days_due}</td>
                  <td>₹{Number(detail.total_overdue_interest).toFixed(2)}</td>
                  <td>₹{Number(detail.overdue_paid).toFixed(2)}</td>
                  <td>₹{Number(detail.remaining_overdue).toFixed(2)}</td>
                  <td>
                    <input
                      type="number"
                      value={overduePayAmounts[detail.due_date] || ''}
                      onChange={(e) => setOverduePayAmounts(prev => ({ ...prev, [detail.due_date]: e.target.value }))}
                      placeholder="Overdue amount"
                    />
                  </td>
                  <td><button onClick={() => handleOverduePayment(detail)}>Pay Overdue</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Payment Form */}
      <div className="payment-form">
        <h3>Make Payment</h3>
        <div>
          <label>Due Date:</label>
          <select value={selectedDueDate} onChange={(e) => setSelectedDueDate(e.target.value)}>
            <option value="">Select Due Date</option>
            {emiDetails.map((emi, index) => (
              <option key={index} value={emi.due_date}>
                {formatDateToDDMMYYYY(emi.due_date)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Payment Amount:</label>
          <input
            type="number"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            placeholder="Enter amount"
          />
          {selectedDueDate && (() => {
            const emiRow = emiDetails.find(e => e.due_date === selectedDueDate);
            if (!emiRow) return null;
            const remaining = Number(emiRow.remaining_amount) || 0;
            const amount = Number(paymentAmount) || 0;
            const excess = amount > remaining ? amount - remaining : 0;
            return (
              <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                <div>Remaining for this EMI: ₹{remaining.toFixed(2)}</div>
                {excess > 0 && (
                  <div style={{ color: '#28a745', fontWeight: 'bold', marginTop: '3px' }}>
                    Excess ₹{excess.toFixed(2)} will be applied to next EMI
                  </div>
                )}
              </div>
            );
          })()}
        </div>
        <div>
  <label>Payment Mode:</label>
  <select
    value={paymentMode}
    onChange={(e) => setPaymentMode(e.target.value)}
  >
    <option value="">Select</option>
    <option value="Cash">Cash</option>
    <option value="UPI">UPI</option>
    <option value="Bank Transfer">Bank Transfer</option>
    <option value="Cheque">Cheque</option>
  </select>
</div>
        <div>
          <label>Transaction ID:</label>
          <input
            type="text"
            value={transactionId}
            onChange={(e) => setTransactionId(e.target.value)}
            placeholder="Enter Transaction ID"
          />
        </div>
        <button onClick={handlePayment}>Submit Payment</button>
      </div>

      {/* Slip Button — reprint the payment(s) just recorded */}
      {isPaymentSuccessful && lastInsertedPayments.length > 0 && (
        <button
          onClick={() => lastInsertedPayments.forEach((payment) => generateSlip({ payment }))}
          className="slip-button"
        >
          {lastInsertedPayments.length > 1 ? 'Reprint New Slips' : 'Reprint New Slip'}
        </button>
      )}

      {/* View All Slips Modal Trigger */}
      <div style={{ marginTop: 10 }}>
        <button onClick={openAllSlips}>View All Payment Slips</button>
      </div>

      {showSlipModal && (
        <div className="modal-overlay" onClick={closeAllSlips}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {slipFilterDueDate
                  ? `Payment Slips — Due ${formatDateToDDMMYYYY(slipFilterDueDate)}`
                  : 'Payment Slips'}
              </h3>
              <button className="modal-close" onClick={closeAllSlips}>×</button>
            </div>
            <div className="modal-body">
              {(transactions || [])
                .filter((tx) => !slipFilterDueDate || tx.due_date === slipFilterDueDate)
                .map((tx, idx) => (
                <div key={tx.id || idx} className="slip-row">
                  <div><strong>Due:</strong> {formatDateToDDMMYYYY(tx.due_date)}</div>
                  <div><strong>Date:</strong> {tx.payment_date ? formatDateToDDMMYYYY(tx.payment_date) : '-'}</div>
                  <div><strong>Mode:</strong> {tx.payment_mode}</div>
                  <div><strong>Txn:</strong> {tx.transaction_id || 'N/A'}</div>
                  <div><strong>Amount:</strong> ₹{Number(tx.amount).toFixed(2)}</div>
                  <div>
                    <button onClick={() => generateSlip({ payment: tx })}>Print</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanPayment;
